/**
 * Article Creator API Keys Service
 *
 * Manages API keys for RSS feed access.
 * Uses the shared /api-keys endpoints, filtering by article-creator:rss permission.
 */

import { api } from './api';

export interface ArticleCreatorApiKey {
  id: string;
  companyId: string;
  name: string;
  keyPreview?: string; // Last 4 chars for display
  permissions: string[];
  isActive: boolean;
  type?: 'general' | 'webhook';
  lastUsedAt?: string;
  expiresAt?: string;
  createdAt: string;
  createdBy: string;
}

export interface CreateApiKeyInput {
  name: string;
  expiresAt?: string;
}

// API returns key data with `key` field containing the raw key (shown once)
export interface CreateApiKeyResponse {
  key: string;
  id: string;
  name: string;
  companyId: string;
  permissions: string[];
  isActive: boolean;
  createdAt: string;
  createdBy: string;
}

const KEYS_ENDPOINT = '/api-keys';
const ARTICLE_CREATOR_PERMISSION = 'article-creator:rss';

export const ApiKeysService = {
  /**
   * Get all API keys for a company that have article-creator:rss permission
   * @param companyId - Optional company ID. If not provided, uses current user's company.
   */
  async getKeys(companyId?: string): Promise<ArticleCreatorApiKey[]> {
    const endpoint = companyId
      ? `${KEYS_ENDPOINT}?companyId=${encodeURIComponent(companyId)}`
      : KEYS_ENDPOINT;
    const response = await api.get<ArticleCreatorApiKey[]>(endpoint);
    if (!response.success) {
      throw new Error(response.error?.message || 'Failed to fetch API keys');
    }
    // Filter to only keys with article-creator:rss permission
    return (response.data || []).filter(key =>
      key.permissions?.includes(ARTICLE_CREATOR_PERMISSION)
    );
  },

  /**
   * Create a new API key with article-creator:rss permission
   * Returns the full key (only shown once) and key metadata
   * @param input - Key name and optional expiration
   * @param companyId - Optional company ID. If not provided, uses current user's company.
   */
  async createKey(input: CreateApiKeyInput, companyId?: string): Promise<CreateApiKeyResponse> {
    const endpoint = companyId
      ? `${KEYS_ENDPOINT}?companyId=${encodeURIComponent(companyId)}`
      : KEYS_ENDPOINT;
    const response = await api.post<CreateApiKeyResponse>(endpoint, {
      name: input.name,
      permissions: [ARTICLE_CREATOR_PERMISSION],
      expiresAt: input.expiresAt,
    });
    if (!response.success || !response.data) {
      throw new Error(response.error?.message || 'Failed to create API key');
    }
    return response.data;
  },

  /**
   * Revoke an API key (marks as inactive)
   */
  async revokeKey(keyId: string): Promise<void> {
    const response = await api.post(`${KEYS_ENDPOINT}/${keyId}/revoke`);
    if (!response.success) {
      throw new Error(response.error?.message || 'Failed to revoke API key');
    }
  },

  /**
   * Delete an API key permanently
   */
  async deleteKey(keyId: string): Promise<void> {
    const response = await api.delete(`${KEYS_ENDPOINT}/${keyId}`);
    if (!response.success) {
      throw new Error(response.error?.message || 'Failed to delete API key');
    }
  },
};

export default ApiKeysService;
